// bowel_movement schema + Bedrock tool (PLAN.md §6.1 Batch 6, D-22).
// GI event — sensitive but clinically rich. Pure data — safe for API + web SPA.

export const BRISTOL_MIN = 1;
export const BRISTOL_MAX = 7;

export const BOWEL_URGENCIES = ["none", "mild", "urgent"] as const;
export type BowelUrgency = (typeof BOWEL_URGENCIES)[number];

// "yellow" (D-BM-5): pale-yellow/greasy stool — steatorrhea / fat malabsorption.
export const BOWEL_COLORS = ["normal", "pale", "yellow", "dark", "black", "red", "green"] as const;
export type BowelColor = (typeof BOWEL_COLORS)[number];

export const BOWEL_VOLUMES = ["small", "normal", "large"] as const;
export type BowelVolume = (typeof BOWEL_VOLUMES)[number];

/** Payload for a `bowel_movement` event (PLAN.md §6.1 Batch 6). */
export interface BowelMovementPayload {
  occurredAt: string; // R — auto-now per D-10
  bristolScale?: number; // O — 1..7 (Bristol Stool Scale); omit when user declines
  urgency?: BowelUrgency; // O
  blood?: boolean; // O
  mucus?: boolean; // O
  painPresent?: boolean; // O
  painSeverity?: number; // O — 1..10 (only if painPresent)
  color?: BowelColor; // O
  volume?: BowelVolume; // O
  straining?: boolean; // O
  incomplete?: boolean; // O
  notes?: string; // O
  /** Confirmed "no bowel movement this day" marker written by the catch-up flow
   *  (CATCHUP_WIDGETS_PLAN.md D-CU-4/D-CU-18). When true, all descriptive fields
   *  (bristolScale, color, urgency, blood, mucus, painPresent, volume) must be absent,
   *  and the event must NOT count as a movement anywhere. */
  noMovement?: boolean;
}

// ── Regularity dashboard constants + helpers (D-BM-1, D-BM-2) ───────────────

/** Default frequency target: 1 movement per day. */
export const DEFAULT_BM_DAILY_GOAL = 1;
/** Days since last movement before the hero turns amber. */
export const DEFAULT_BM_SOFT_DAYS = 2;
/** Days since last movement before the hero turns red (constipation flag). */
export const DEFAULT_BM_HARD_DAYS = 3;

export type BristolBand = "hard" | "normal" | "loose";

/** Map a Bristol scale value to a clinical band for calendar tinting. */
export function bristolBand(scale: number): BristolBand {
  if (scale <= 2) return "hard";
  if (scale <= 4) return "normal";
  return "loose";
}

/** Tailwind text-color classes for a Bristol band. */
export function bristolBandColor(band: BristolBand): string {
  switch (band) {
    case "hard":   return "text-red-600 dark:text-red-400";
    case "normal": return "text-emerald-600 dark:text-emerald-400";
    case "loose":  return "text-amber-500 dark:text-amber-400";
  }
}

/** Short Bristol label (e.g. "Type 4 — Smooth sausage"). */
export function bristolLabel(scale: number): string {
  const LABELS: Record<number, string> = {
    1: "Type 1 — Hard lumps",
    2: "Type 2 — Lumpy sausage",
    3: "Type 3 — Cracked sausage",
    4: "Type 4 — Smooth sausage",
    5: "Type 5 — Soft blobs",
    6: "Type 6 — Fluffy pieces",
    7: "Type 7 — Watery",
  };
  return LABELS[scale] ?? `Type ${scale}`;
}

// ── Volume label + unified summary (BOWEL_DETAIL_DISPLAY_PLAN.md) ───────────

export const BOWEL_VOLUME_LABEL: Record<BowelVolume, string> = {
  small: "Small",
  normal: "Normal",
  large: "Large",
};

export interface BowelFlag {
  key: "volume" | "urgency" | "color" | "blood" | "mucus" | "straining" | "incomplete" | "pain";
  label: string;
}

export interface BowelMovementSummary {
  headline: string;
  flags: BowelFlag[];
}

/**
 * Human-readable summary of a bowel_movement payload: a headline (Bristol
 * type if recorded, else volume, else a bare fallback) plus an ordered list
 * of secondary flags for whatever else was recorded. Volume is only listed
 * as a flag when Bristol is also present — when Bristol is absent, volume is
 * already the headline and would otherwise be shown twice. Shared by the
 * Logs page (eventSummary.tsx) and the calendar day-detail list
 * (BowelPane.tsx) so both surface the same information (BOWEL_DETAIL_DISPLAY_PLAN.md).
 */
export function summarizeBowelMovement(p: Partial<BowelMovementPayload>): BowelMovementSummary {
  if (p.noMovement) return { headline: "No bowel movement (confirmed)", flags: [] };

  const headline =
    typeof p.bristolScale === "number"
      ? `Bristol ${p.bristolScale}`
      : p.volume
        ? `${BOWEL_VOLUME_LABEL[p.volume]} movement`
        : "Movement logged";

  const flags: BowelFlag[] = [];
  if (p.volume && typeof p.bristolScale === "number") {
    flags.push({ key: "volume", label: BOWEL_VOLUME_LABEL[p.volume] });
  }
  if (p.urgency && p.urgency !== "none") flags.push({ key: "urgency", label: `${p.urgency} urgency` });
  if (p.color && p.color !== "normal") flags.push({ key: "color", label: p.color });
  if (p.blood) flags.push({ key: "blood", label: "blood" });
  if (p.mucus) flags.push({ key: "mucus", label: "mucus" });
  if (p.straining) flags.push({ key: "straining", label: "straining" });
  if (p.incomplete) flags.push({ key: "incomplete", label: "incomplete" });
  if (p.painPresent) flags.push({ key: "pain", label: `pain ${p.painSeverity ?? "?"}/10` });

  return { headline, flags };
}

export type ConstipationLevel = "ok" | "soft" | "hard";

/** Returns the constipation urgency level given elapsed days since the last BM. */
export function constipationLevel(
  daysSince: number,
  softDays: number = DEFAULT_BM_SOFT_DAYS,
  hardDays: number = DEFAULT_BM_HARD_DAYS
): ConstipationLevel {
  if (daysSince >= hardDays) return "hard";
  if (daysSince >= softDays) return "soft";
  return "ok";
}

// ── Laxative/fiber identification (D-BM-6, Phase 3 correlations) ─────────────

/**
 * Common laxative, stool-softener, and fiber-supplement ingredient/brand names.
 * Used by isLaxativeMed() for the Phase 3 correlation strip.
 */
export const LAXATIVE_KEYWORDS = [
  "docusate", "colace", "surfak",
  "senna", "senokot", "ex-lax", "exlax",
  "bisacodyl", "dulcolax", "correctol",
  "polyethylene glycol", "miralax", "glycolax",
  "lactulose", "enulose", "generlac", "kristalose",
  "psyllium", "metamucil", "konsyl", "fiberall",
  "magnesium hydroxide", "milk of magnesia", "phillips",
  "methylcellulose", "citrucel",
  "calcium polycarbophil", "fibercon",
  "mineral oil",
  "magnesium citrate",
  "glycerin suppository",
  "lubiprostone", "amitiza",
  "linaclotide", "linzess",
  "prucalopride", "motegrity",
  "plecanatide", "trulance",
] as const;

/** Returns true if a medication name appears to be a laxative, stool softener,
 *  or fiber supplement — for the regularity correlation strip (D-BM-6). */
export function isLaxativeMed(name: string): boolean {
  const lower = name.toLowerCase();
  return (LAXATIVE_KEYWORDS as readonly string[]).some((kw) => lower.includes(kw));
}

/**
 * Bedrock tool: record a bowel movement. Needs the Bristol Stool Scale value
 * (1 = hard lumps … 7 = entirely liquid). Capture color/urgency/blood/etc. only
 * when the user mentions them. Omit occurredAt unless the user stated a time.
 */
export const RECORD_BOWEL_TOOL = {
  name: "record_bowel_movement",
  description:
    "Record a bowel movement. bristolScale (1 = hard lumps, 7 = watery) is preferred but optional — " +
    "omit it only if the user explicitly declines to provide a rating. If the user describes " +
    "consistency qualitatively (e.g. 'watery', 'loose'), map it to the scale (watery = 7, loose = 6). " +
    "Capture color, urgency, blood, mucus, pain, etc. only if mentioned. " +
    "color='yellow' means pale-yellow/greasy stool (steatorrhea / fat malabsorption). " +
    "Omit occurredAt unless a time was given.",
  input_schema: {
    type: "object",
    properties: {
      occurredAt: { type: "string", description: "ISO-8601 with UTC offset. Omit if no time given." },
      bristolScale: { type: "number", description: "1–7 Bristol Stool Scale." },
      urgency: { type: "string", enum: [...BOWEL_URGENCIES] },
      blood: { type: "boolean" },
      mucus: { type: "boolean" },
      painPresent: { type: "boolean" },
      painSeverity: { type: "number", description: "1–10, only if pain present." },
      color: { type: "string", enum: [...BOWEL_COLORS] },
      volume: { type: "string", enum: [...BOWEL_VOLUMES] },
      straining: { type: "boolean" },
      incomplete: { type: "boolean" },
      notes: { type: "string" },
    },
    required: [],
  },
} as const;
